import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCarportAdj, selectCarportAdjs } from './selectCarportAdj.ts';
import { selectGarageAdj, selectGarageAdjs } from './selectGarageAdj.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateGarageCarportAdj = (
  garageAdj: AloftPrice = AloftPrice.zero,
  carportAdj: AloftPrice = AloftPrice.zero,
) => [garageAdj, carportAdj].reduce((acc: AloftPrice, curr) => acc.add(curr), AloftPrice.zero);

const selectGarageCarportAdj = createPriceSelector(
  [selectGarageAdj, selectCarportAdj],
  calculateGarageCarportAdj,
);

const selectGarageCarportAdjs = createSelector(
  [selectGarageAdjs, selectCarportAdjs, selectCompSequence],
  (garageAdjs, carportAdjs, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateGarageCarportAdj(garageAdjs[propID], carportAdjs[propID]);

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);

const selectAppliedGarageCarportAdj = createPriceSelector(
  [selectGarageCarportAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'garageCarport', adjSettings);
  },
);

export const selectAppliedGarageCarportAdjDisplay = createSelector(
  [selectAppliedGarageCarportAdj],
  (adj) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedGarageCarportAdjsDisplay = createSelector(
  [selectGarageCarportAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (garageCarportAdjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(garageCarportAdjs).reduce(
      (acc: Record<string, { value: string; color: string }>, propID) => {
        const adj = garageCarportAdjs[propID];
        const manualAdjustment = manualAdjustmentEntities[propID]?.garageCarport;
        if (isDefined(manualAdjustment)) {
          const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
          acc[propID] = {
            value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
            color: getColorFromAdjustment(manualAdjustmentPrice),
          };
        } else if (isDefined(adj)) {
          acc[propID] = {
            value: getStringValueFromAdjustment(
              applyRoundingAndThresholds(garageCarportAdjs[propID], 'garageCarport', adjSettings),
            ),
            color: getColorFromAdjustment(garageCarportAdjs[propID]),
          };
        }
        return acc;
      },
      {},
    ),
);
